// Faza 9 — Comparison / Decision stranice ("vs" + "kada šta") koje hvataju
// visoko-konvertujuće upite tipa "kiropraktika vs fizikalna terapija".

export interface CompareI18n {
  metaTitle: string;
  metaDesc: string;
  h1: string;
  intro: string;
  /** Sažetak (sa-na-prvi-pogled) */
  tldr: string;
  /** Tabela razlika — niz redova { aspect, a, b } */
  table: { aspect: string; a: string; b: string }[];
  /** Kada birati varijantu A */
  whenA: string[];
  /** Kada birati varijantu B */
  whenB: string[];
  /** Zajednički zaključak */
  verdict: string;
  faqs: { question: string; answer: string }[];
}

export interface Comparison {
  slug: string; // npr. "kiropraktika-vs-fizikalna-terapija"
  /** Naziv opcije A (npr. "Kiropraktika") */
  a: { sr: string; en: string; ru: string };
  /** Naziv opcije B */
  b: { sr: string; en: string; ru: string };
  /** Interni linkovi ka A i B uslugama */
  links: { aPath: string; bPath: string };
  /** Povezane dijagnoze za internal linking */
  relatedDiagnoses?: string[];
  i18n: { sr: CompareI18n; en: CompareI18n; ru: CompareI18n };
}

export const COMPARISONS: Comparison[] = [
  {
    slug: 'kiropraktika-vs-fizikalna-terapija',
    a: { sr: 'Kiropraktika', en: 'Chiropractic', ru: 'Хиропрактика' },
    b: { sr: 'Fizikalna terapija', en: 'Physical therapy', ru: 'Физиотерапия' },
    links: { aPath: '/kiropraktika-novi-beograd', bPath: '/fizikalna-terapija-novi-beograd' },
    relatedDiagnoses: ['bol-u-ledjima-tretman-beograd', 'bol-u-vratu-tretman-beograd', 'diskus-hernija-tretman-beograd'],
    i18n: {
      sr: {
        metaTitle: 'Kiropraktika vs Fizikalna terapija — šta izabrati | PHYSIOWRLD',
        metaDesc: 'Kiropraktika ili fizikalna terapija? Razlike, prednosti i kada koju izabrati kod bola u leđima i vratu. Klinički vodič PHYSIOWRLD klinike.',
        h1: 'Kiropraktika vs fizikalna terapija — šta izabrati',
        intro: 'Kiropraktika i fizikalna terapija često se preklapaju u tretmanu bola u leđima i vratu. Razlika nije u tome „koja je bolja", već koja je primerenija konkretnoj dijagnozi i fazi simptoma.',
        tldr: 'Kiropraktika daje brzo olakšanje kod akutnog blokiranja segmenta kičme. Fizikalna terapija gradi dugoročno rešenje kroz vežbe, modalitete i edukaciju pacijenta. U praksi se često kombinuju.',
        table: [
          { aspect: 'Glavni pristup', a: 'Manuelna adjustacija kičme', b: 'Vežbe, modaliteti, edukacija' },
          { aspect: 'Trajanje seanse', a: '20–30 minuta', b: '30–60 minuta' },
          { aspect: 'Tipičan broj seansi', a: '3–6 (kratko)', b: '6–12 (programski)' },
          { aspect: 'Brzina olakšanja', a: 'Često odmah po seansi', b: 'Postupno, kroz nekoliko nedelja' },
          { aspect: 'Fokus', a: 'Pokretljivost segmenta', b: 'Snaga, kontrola pokreta, ishod' },
          { aspect: 'Najbolje za', a: 'Akutni blok, posturalni bol', b: 'Hronični bol, postop, sportske povrede' },
        ],
        whenA: [
          'Iznenadno „zaglavljenje" u vratu ili leđima',
          'Glavobolja cervikalnog porekla',
          'Posturalni bol bez radikularnih znakova',
          'Kada je cilj brzo olakšanje pre nastavka rada',
        ],
        whenB: [
          'Hronični bol u leđima (preko 6 nedelja)',
          'Postoperativna rehabilitacija (kičma, kolena, rame)',
          'Sportske povrede koje zahtevaju progresivno opterećenje',
          'Diskus hernija sa neurološkim simptomima',
        ],
        verdict: 'U PHYSIOWRLD klinici često kombinujemo kiropraktiku za inicijalno otpuštanje segmenta sa fizikalnom terapijom koja stabilizuje rezultat kroz vežbe i modalitete (HILT laser, elektroterapija).',
        faqs: [
          { question: 'Mogu li da koristim oba istovremeno?', answer: 'Da. U većini slučajeva pacijenti dobijaju nekoliko kiropraktičkih seansi praćenih programom fizikalne terapije — pristup koji praksa i istraživanja podržavaju.' },
          { question: 'Da li je kiropraktika bezbedna kod diskus hernije?', answer: 'Uz pravilnu kliničku procenu i izbor tehnika, da. Visoko-velocitarne manipulacije se izbegavaju u akutnoj fazi sa radikularnim znacima.' },
          { question: 'Šta brže pomaže kod akutnog bola u leđima?', answer: 'Kiropraktika tipično daje brže subjektivno olakšanje, ali bez nastavka fizikalne terapije rizik od recidiva ostaje visok.' },
        ],
      },
      en: {
        metaTitle: 'Chiropractic vs Physical Therapy — which to choose | PHYSIOWRLD',
        metaDesc: 'Chiropractic or physical therapy? Differences, benefits and when to choose which for back and neck pain. Clinical guide by PHYSIOWRLD clinic.',
        h1: 'Chiropractic vs physical therapy — which to choose',
        intro: 'Chiropractic and physical therapy often overlap for back and neck pain. The difference is not "which is better" but which fits the diagnosis and symptom phase.',
        tldr: 'Chiropractic provides quick relief for acute segmental blocking. Physical therapy builds a long-term solution through exercise, modalities and patient education. They are commonly combined.',
        table: [
          { aspect: 'Main approach', a: 'Manual spinal adjustment', b: 'Exercise, modalities, education' },
          { aspect: 'Session length', a: '20–30 min', b: '30–60 min' },
          { aspect: 'Typical course', a: '3–6 sessions', b: '6–12 sessions' },
          { aspect: 'Time to relief', a: 'Often immediate', b: 'Gradual, over weeks' },
          { aspect: 'Focus', a: 'Segment mobility', b: 'Strength, motor control, outcome' },
          { aspect: 'Best for', a: 'Acute block, postural pain', b: 'Chronic, post-op, sports injuries' },
        ],
        whenA: [
          'Sudden neck or back "lock"',
          'Cervicogenic headache',
          'Postural pain without radicular signs',
          'When fast relief is needed before resuming work',
        ],
        whenB: [
          'Chronic back pain (>6 weeks)',
          'Post-operative rehab (spine, knee, shoulder)',
          'Sports injuries needing progressive load',
          'Disc herniation with neurological signs',
        ],
        verdict: 'At PHYSIOWRLD we commonly combine chiropractic for initial release with physical therapy that stabilizes the result via exercise and modalities (HILT laser, electrotherapy).',
        faqs: [
          { question: 'Can I use both at the same time?', answer: 'Yes. Patients often receive a few chiropractic sessions followed by a physical therapy program — an approach supported by clinical practice and research.' },
          { question: 'Is chiropractic safe for disc herniation?', answer: 'With proper assessment and technique selection, yes. High-velocity manipulation is avoided in the acute radicular phase.' },
          { question: 'What helps faster for acute back pain?', answer: 'Chiropractic typically delivers faster subjective relief, but without follow-up physical therapy the recurrence risk stays high.' },
        ],
      },
      ru: {
        metaTitle: 'Хиропрактика или физиотерапия — что выбрать | PHYSIOWRLD',
        metaDesc: 'Хиропрактика или физиотерапия? Различия и когда что выбирать при боли в спине и шее. Клинический гид PHYSIOWRLD.',
        h1: 'Хиропрактика и физиотерапия — что выбрать',
        intro: 'Хиропрактика и физиотерапия часто пересекаются при болях в спине и шее. Вопрос не «что лучше», а что подходит конкретному диагнозу и фазе симптомов.',
        tldr: 'Хиропрактика даёт быстрое облегчение при остром блоке. Физиотерапия строит долгосрочный результат через упражнения и модальности. На практике их часто сочетают.',
        table: [
          { aspect: 'Подход', a: 'Мануальная адъюстация', b: 'Упражнения и модальности' },
          { aspect: 'Длительность сеанса', a: '20–30 мин', b: '30–60 мин' },
          { aspect: 'Курс', a: '3–6 сеансов', b: '6–12 сеансов' },
          { aspect: 'Скорость облегчения', a: 'Часто сразу', b: 'Постепенно' },
          { aspect: 'Фокус', a: 'Подвижность сегмента', b: 'Сила, контроль движения' },
          { aspect: 'Лучше для', a: 'Острый блок', b: 'Хроническая боль, постоп' },
        ],
        whenA: ['Внезапный «блок» шеи или спины', 'Цервикогенная головная боль', 'Постуральная боль без радикулярных признаков'],
        whenB: ['Хроническая боль', 'Послеоперационная реабилитация', 'Спортивные травмы', 'Грыжа диска с неврологией'],
        verdict: 'В PHYSIOWRLD часто сочетаем хиропрактику для первичного освобождения сегмента и физиотерапию для стабилизации результата.',
        faqs: [
          { question: 'Можно ли совмещать?', answer: 'Да, это распространённый и поддержанный исследованиями подход.' },
          { question: 'Безопасна ли хиропрактика при грыже?', answer: 'При корректной оценке — да. Высокоскоростные манипуляции избегают в остром радикулярном периоде.' },
          { question: 'Что быстрее снимает острую боль?', answer: 'Хиропрактика быстрее даёт субъективное облегчение, но без физиотерапии риск рецидива выше.' },
        ],
      },
    },
  },
  {
    slug: 'shockwave-vs-ultrazvuk',
    a: { sr: 'Shockwave terapija', en: 'Shockwave therapy', ru: 'УВТ (Shockwave)' },
    b: { sr: 'Terapeutski ultrazvuk', en: 'Therapeutic ultrasound', ru: 'Ультразвуковая терапия' },
    links: { aPath: '/shockwave-terapija-novi-beograd', bPath: '/fizikalna-terapija-novi-beograd' },
    relatedDiagnoses: ['petni-trn', 'teniski-lakat-tretman-beograd', 'ahilova-tetiva-tretman-beograd'],
    i18n: {
      sr: {
        metaTitle: 'Shockwave vs ultrazvuk — efikasnost i razlike | PHYSIOWRLD',
        metaDesc: 'Shockwave (AWT) ili terapeutski ultrazvuk za tendinopatije i petni trn? Klinički vodič PHYSIOWRLD klinike.',
        h1: 'Shockwave vs terapeutski ultrazvuk',
        intro: 'Iako se obe terapije zasnivaju na akustičnoj energiji, mehanizam delovanja i klinička indikacija im se značajno razlikuju.',
        tldr: 'Shockwave je terapija prvog izbora kod hroničnih tendinopatija (petni trn, teniski lakat, ahilova). Terapeutski ultrazvuk se koristi kao adjuvantni modalitet kod manjih, akutnijih stanja.',
        table: [
          { aspect: 'Tip energije', a: 'Akustični talasi visoke energije', b: 'Akustični talasi niske energije (1–3 MHz)' },
          { aspect: 'Mehanizam', a: 'Mehanički + neovaskularizacija', b: 'Termalni + mikromasaža' },
          { aspect: 'Trajanje seanse', a: '5–10 minuta', b: '5–10 minuta' },
          { aspect: 'Broj seansi', a: '3–5, na nedeljnom razmaku', b: '6–10 (svakodnevno ili 3x nedeljno)' },
          { aspect: 'Indikacija', a: 'Hronične tendinopatije, kalcifikati', b: 'Manja akutna stanja, kontrakture' },
          { aspect: 'Dokazi', a: 'Jaki RCT podaci kod petnog trna i teniskog lakta', b: 'Slabiji, više kao adjuvant' },
        ],
        whenA: [
          'Petni trn / plantarni fascitis duži od 6 nedelja',
          'Teniski ili golferski lakat',
          'Ahilova tendinopatija',
          'Kalcifikati u rotatornoj manžetni',
        ],
        whenB: [
          'Manja akutna povreda mekog tkiva',
          'Posttraumatska kontraktura zgloba',
          'Hematomi i edemi u resorpciji',
          'Kao deo šireg fizikalnog programa',
        ],
        verdict: 'Za hronične tendinopatije Shockwave ima jaču evidence-based osnovu. Ultrazvuk ostaje koristan adjuvantni modalitet u sklopu šire rehabilitacije.',
        faqs: [
          { question: 'Koliko Shockwave seansi je potrebno?', answer: 'Standardni protokol je 3–5 seansi sa razmakom od 5–7 dana. Procena efekta je 6–8 nedelja od poslednje seanse.' },
          { question: 'Da li ultrazvuk uopšte ima smisla?', answer: 'Da, kao adjuvantni modalitet kod akutnih stanja i kontraktura, ne kao samostalna terapija hroničnih tendinopatija.' },
          { question: 'Koliko boli Shockwave?', answer: 'Postoji prolazna nelagoda u toku seanse koja se kontroliše podešavanjem energije. Bez kasnijih ozbiljnih neželjenih efekata.' },
        ],
      },
      en: {
        metaTitle: 'Shockwave vs Ultrasound — effectiveness compared | PHYSIOWRLD',
        metaDesc: 'Shockwave (AWT) or therapeutic ultrasound for tendinopathies and heel spur? Clinical guide by PHYSIOWRLD clinic.',
        h1: 'Shockwave vs therapeutic ultrasound',
        intro: 'Both modalities use acoustic energy, but the mechanism and clinical indication differ significantly.',
        tldr: 'Shockwave is first-line for chronic tendinopathies (heel spur, tennis elbow, Achilles). Therapeutic ultrasound is an adjuvant modality for milder, more acute issues.',
        table: [
          { aspect: 'Energy type', a: 'High-energy acoustic waves', b: 'Low-energy acoustic waves (1–3 MHz)' },
          { aspect: 'Mechanism', a: 'Mechanical + neovascularization', b: 'Thermal + micromassage' },
          { aspect: 'Session length', a: '5–10 min', b: '5–10 min' },
          { aspect: 'Course', a: '3–5 weekly sessions', b: '6–10 (daily or 3x/week)' },
          { aspect: 'Indication', a: 'Chronic tendinopathies, calcifications', b: 'Minor acute issues, contractures' },
          { aspect: 'Evidence', a: 'Strong RCT data for heel spur, tennis elbow', b: 'Weaker, mainly adjuvant' },
        ],
        whenA: ['Plantar fasciitis >6 weeks', 'Tennis or golfer\'s elbow', 'Achilles tendinopathy', 'Rotator cuff calcifications'],
        whenB: ['Minor acute soft-tissue injury', 'Post-traumatic joint contracture', 'Hematoma/edema resorption', 'Part of broader physical program'],
        verdict: 'For chronic tendinopathies Shockwave has stronger evidence. Ultrasound remains a useful adjuvant within broader rehab.',
        faqs: [
          { question: 'How many Shockwave sessions?', answer: 'Standard protocol: 3–5 sessions, 5–7 days apart. Effect assessed 6–8 weeks after last session.' },
          { question: 'Does ultrasound make sense at all?', answer: 'Yes, as an adjuvant for acute issues and contractures — not as standalone therapy for chronic tendinopathies.' },
          { question: 'Is Shockwave painful?', answer: 'Transient discomfort during the session, controlled by adjusting energy. No serious aftereffects.' },
        ],
      },
      ru: {
        metaTitle: 'УВТ или ультразвук — что эффективнее | PHYSIOWRLD',
        metaDesc: 'Shockwave (УВТ) или терапевтический ультразвук при тендинопатиях и пяточной шпоре? Клинический гид PHYSIOWRLD.',
        h1: 'УВТ против терапевтического ультразвука',
        intro: 'Обе методики используют акустическую энергию, но механизм и клинические показания различны.',
        tldr: 'УВТ — терапия первой линии при хронических тендинопатиях. Ультразвук — адъювантная методика при более лёгких и острых состояниях.',
        table: [
          { aspect: 'Тип энергии', a: 'Высокоэнергетические волны', b: 'Низкоэнергетические (1–3 МГц)' },
          { aspect: 'Механизм', a: 'Механика + неоваскуляризация', b: 'Термический + микромассаж' },
          { aspect: 'Сеанс', a: '5–10 мин', b: '5–10 мин' },
          { aspect: 'Курс', a: '3–5 еженедельных', b: '6–10 (ежедневно или 3 р/нед)' },
          { aspect: 'Показания', a: 'Хронические тендинопатии', b: 'Лёгкие острые состояния' },
          { aspect: 'Доказательства', a: 'Сильные данные RCT', b: 'Слабее, как адъювант' },
        ],
        whenA: ['Плантарный фасциит >6 нед', 'Локоть теннисиста/гольфиста', 'Ахиллова тендинопатия', 'Кальцификаты ротаторов'],
        whenB: ['Лёгкая острая травма', 'Посттравматическая контрактура', 'Резорбция гематом', 'В составе программы'],
        verdict: 'При хронических тендинопатиях у УВТ более сильная доказательная база. Ультразвук остаётся полезным адъювантом.',
        faqs: [
          { question: 'Сколько сеансов УВТ?', answer: 'Стандарт — 3–5 сеансов с интервалом 5–7 дней. Эффект оценивают через 6–8 недель.' },
          { question: 'Имеет ли смысл ультразвук?', answer: 'Да, как адъювант при острых состояниях и контрактурах.' },
          { question: 'Болезненна ли УВТ?', answer: 'Преходящий дискомфорт, контролируемый настройкой энергии.' },
        ],
      },
    },
  },
  {
    slug: 'masaza-vs-fizikalna-terapija',
    a: { sr: 'Terapijska masaža', en: 'Therapeutic massage', ru: 'Терапевтический массаж' },
    b: { sr: 'Fizikalna terapija', en: 'Physical therapy', ru: 'Физиотерапия' },
    links: { aPath: '/masaza-novi-beograd', bPath: '/fizikalna-terapija-novi-beograd' },
    relatedDiagnoses: ['bol-u-vratu-tretman-beograd', 'bol-u-ledjima-tretman-beograd'],
    i18n: {
      sr: {
        metaTitle: 'Masaža vs fizikalna terapija — kada šta | PHYSIOWRLD',
        metaDesc: 'Da li je dovoljna masaža ili je potrebna fizikalna terapija? Klinički vodič za pacijente PHYSIOWRLD klinike.',
        h1: 'Masaža vs fizikalna terapija — kada šta',
        intro: 'Masaža i fizikalna terapija nisu zamena jedna za drugu. Masaža radi na mišićnoj tenziji i regeneraciji, fizikalna terapija leči konkretnu dijagnozu.',
        tldr: 'Ako je tegoba mišićna napetost ili stres — masaža je dovoljna. Ako postoji bol koji se vraća, ograničen pokret, trnjenje ili dijagnoza — neophodna je fizikalna terapija.',
        table: [
          { aspect: 'Cilj', a: 'Relaksacija, drenaža, regeneracija', b: 'Lečenje konkretne dijagnoze' },
          { aspect: 'Procena', a: 'Bez kliničke procene', b: 'Anamneza i funkcionalni testovi' },
          { aspect: 'Trajanje', a: '30–60 min', b: '30–60 min' },
          { aspect: 'Učestalost', a: 'Po potrebi / preventivno', b: 'Programski (2–3x nedeljno)' },
          { aspect: 'Modaliteti', a: 'Manuelna tehnika', b: 'HILT laser, elektroterapija, vežbe' },
          { aspect: 'Plaća zdravstveno', a: 'Ne (estetski tretman)', b: 'Delom (medicinski tretman)' },
        ],
        whenA: [
          'Mišićna napetost od stresa ili dugog sedenja',
          'Regeneracija posle sporta',
          'Hronična ukočenost bez konkretne dijagnoze',
          'Prevencija — kao redovan deo nege tela',
        ],
        whenB: [
          'Bol koji se vraća više od 2 nedelje',
          'Trnjenje, slabost ili ograničen pokret',
          'Postoperativni period',
          'Bilo koja postavljena dijagnoza (hernija, tendinopatija, artroza)',
        ],
        verdict: 'Masaža je deo nege tela, fizikalna terapija je medicinski tretman. Kod akutnog ili dijagnostikovanog stanja masaža bez fizikalne terapije može privremeno ublažiti tegobu, ali ne rešava uzrok.',
        faqs: [
          { question: 'Ako me boli vrat, treba li masaža ili fizikalna terapija?', answer: 'Ako je bol kraći od nedelju dana i bez trnjenja u ruci, masaža je adekvatna. Ako traje duže ili ima neuroloških znakova, potrebna je fizikalna terapija sa procenom.' },
          { question: 'Mogu li raditi i jedno i drugo?', answer: 'Da. U sklopu programa fizikalne terapije masaža se često koristi kao adjuvantni modalitet za pripremu tkiva.' },
          { question: 'Da li je sportska masaža isto što i fizikalna terapija?', answer: 'Ne. Sportska masaža je nega tela orijentisana na regeneraciju, dok fizikalna terapija sportiste prati ciljani protokol povrede.' },
        ],
      },
      en: {
        metaTitle: 'Massage vs Physical Therapy — when which | PHYSIOWRLD',
        metaDesc: 'Is massage enough or do you need physical therapy? Clinical guide for patients of PHYSIOWRLD clinic.',
        h1: 'Massage vs physical therapy — when which',
        intro: 'Massage and physical therapy are not substitutes. Massage works on muscle tension and recovery; physical therapy treats a specific diagnosis.',
        tldr: 'If the complaint is muscle tension or stress — massage is enough. If pain recurs, motion is limited, there is tingling or a diagnosis — physical therapy is required.',
        table: [
          { aspect: 'Goal', a: 'Relaxation, drainage, recovery', b: 'Treating a specific diagnosis' },
          { aspect: 'Assessment', a: 'No clinical assessment', b: 'History and functional tests' },
          { aspect: 'Duration', a: '30–60 min', b: '30–60 min' },
          { aspect: 'Frequency', a: 'As needed / preventive', b: 'Programmatic (2–3x/week)' },
          { aspect: 'Modalities', a: 'Manual technique', b: 'HILT laser, electrotherapy, exercise' },
          { aspect: 'Covered by insurance', a: 'No (wellness)', b: 'Partly (medical)' },
        ],
        whenA: ['Tension from stress or prolonged sitting', 'Post-sport recovery', 'Chronic stiffness without diagnosis', 'Prevention'],
        whenB: ['Pain recurring >2 weeks', 'Tingling, weakness or limited motion', 'Post-op', 'Any established diagnosis'],
        verdict: 'Massage is body care, physical therapy is medical treatment. With an acute or diagnosed condition, massage alone may temporarily relieve symptoms but does not address the cause.',
        faqs: [
          { question: 'Neck pain — massage or PT?', answer: 'If pain is <1 week without arm tingling, massage is fine. Longer or with neurological signs — PT with assessment is needed.' },
          { question: 'Can I do both?', answer: 'Yes. Massage is often used as an adjuvant within a PT program to prepare tissue.' },
          { question: 'Is sports massage the same as PT?', answer: 'No. Sports massage is recovery-oriented body care; PT for athletes follows a targeted injury protocol.' },
        ],
      },
      ru: {
        metaTitle: 'Массаж или физиотерапия — когда что | PHYSIOWRLD',
        metaDesc: 'Достаточно ли массажа или нужна физиотерапия? Клинический гид для пациентов PHYSIOWRLD.',
        h1: 'Массаж и физиотерапия — когда что',
        intro: 'Массаж и физиотерапия не заменяют друг друга. Массаж работает с мышечным напряжением, физиотерапия лечит конкретный диагноз.',
        tldr: 'Если жалоба — напряжение и стресс, достаточно массажа. Если боль возвращается, есть онемение или диагноз — нужна физиотерапия.',
        table: [
          { aspect: 'Цель', a: 'Релакс, дренаж', b: 'Лечение диагноза' },
          { aspect: 'Оценка', a: 'Без клинической оценки', b: 'Анамнез и функциональные тесты' },
          { aspect: 'Длительность', a: '30–60 мин', b: '30–60 мин' },
          { aspect: 'Частота', a: 'По необходимости', b: '2–3 раза в неделю' },
          { aspect: 'Модальности', a: 'Мануальная техника', b: 'HILT, электротерапия, упражнения' },
        ],
        whenA: ['Напряжение от стресса/сидения', 'Восстановление после спорта', 'Хроническая скованность без диагноза'],
        whenB: ['Боль >2 недель', 'Онемение, слабость', 'Послеоперационный период', 'Установленный диагноз'],
        verdict: 'Массаж — уход за телом, физиотерапия — медицинское лечение. При остром или диагностированном состоянии массаж не устраняет причину.',
        faqs: [
          { question: 'Боль в шее — массаж или физиотерапия?', answer: 'Если боль до недели без онемения — массаж. Дольше или с неврологией — физиотерапия с оценкой.' },
          { question: 'Можно ли совмещать?', answer: 'Да, массаж часто используют как адъювант в программе физиотерапии.' },
          { question: 'Спортивный массаж = физиотерапия?', answer: 'Нет. Это уход за телом, физиотерапия для спортсменов — целевой протокол травмы.' },
        ],
      },
    },
  },
];

export const COMPARISON_MAP: Record<string, Comparison> = Object.fromEntries(
  COMPARISONS.map((c) => [c.slug, c])
);
